import { useMutation, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '@/shared/api/client'
import { useTelegram } from '@/shared/lib/telegram'

// ============================================================
// Типы (соответствуют backend/src/modules/application/)
// ============================================================

export type ApplicationType = 
  | 'event_signup'
  | 'callback'
  | 'booking'
  | 'tasting'
  | 'certificate'

export interface CreateApplicationInput {
  /** Тип заявки */
  type: ApplicationType
  /** ID товара (для консультации) */
  productId?: string
  /** Название товара */
  productName?: string
  /** ID события (для регистрации) */
  eventId?: string
  /** Название события */
  eventTitle?: string
  /** Имя клиента */
  name: string
  /** Телефон */
  phone?: string
  /** Email */
  email?: string
  /** Сообщение */
  message?: string
  /** Дата бронирования */
  bookingDate?: string
  /** Время бронирования */
  bookingTime?: string
  /** Количество гостей */
  guestsCount?: number
}

export interface Application {
  id: string
  type: ApplicationType
  productId: string | null
  productName: string | null
  eventId: string | null
  eventTitle: string | null
  name: string | null
  phone: string | null
  email: string | null
  message: string | null
  bookingDate: string | null
  bookingTime: string | null
  guestsCount: number | null
  status: 'new' | 'in_progress' | 'completed' | 'cancelled'
  createdAt: string
  updatedAt: string
}

interface ApplicationResponse {
  success: boolean
  data: Application
}

// ============================================================
// API Hooks
// ============================================================

/**
 * Создать заявку
 */
export function useCreateApplication() {
  const queryClient = useQueryClient()
  const { initData } = useTelegram()

  return useMutation({
    mutationFn: async (data: CreateApplicationInput) => {
      return apiClient<ApplicationResponse>('/api/public/applications', {
        method: 'POST',
        body: JSON.stringify(data),
        initData: initData || undefined,
      })
    },
    onSuccess: () => {
      // Инвалидируем кэш событий после создания заявки
      queryClient.invalidateQueries({ queryKey: ['events'] })
    },
  })
}

/**
 * Создать заявку на событие (упрощённый хелпер)
 */
export function useRegisterForEvent() {
  const createApplication = useCreateApplication()
  
  return {
    ...createApplication,
    mutate: (data: {
      eventId: string
      eventTitle: string
      name?: string
      phone?: string
      email?: string
      message?: string
      guestsCount?: number
    }) => {
      createApplication.mutate({
        type: 'event_signup',
        eventId: data.eventId,
        eventTitle: data.eventTitle,
        name: data.name || '',
        phone: data.phone,
        email: data.email,
        message: data.message,
        guestsCount: data.guestsCount,
      })
    },
    mutateAsync: (data: {
      eventId: string
      eventTitle: string
      name?: string
      phone?: string
      email?: string
      message?: string
      guestsCount?: number
    }) => {
      return createApplication.mutateAsync({
        type: 'event_signup',
        eventId: data.eventId,
        eventTitle: data.eventTitle,
        name: data.name || '',
        phone: data.phone,
        email: data.email,
        message: data.message,
        guestsCount: data.guestsCount,
      })
    },
  }
}

/**
 * Запросить обратный звонок
 */
export function useRequestCallback() {
  const createApplication = useCreateApplication()
  
  return {
    ...createApplication,
    mutate: (data: { name?: string; phone: string; message?: string }) => {
      createApplication.mutate({
        type: 'callback',
        name: data.name || '',
        phone: data.phone,
        message: data.message,
      })
    },
    mutateAsync: (data: { name?: string; phone: string; message?: string }) => {
      return createApplication.mutateAsync({
        type: 'callback',
        name: data.name || '',
        phone: data.phone,
        message: data.message,
      })
    },
  }
}
